import {useCallback, useLayoutEffect, useState, type Ref} from 'react';

/** Add a native keyboard scroll target only when the region actually overflows. */
export function useScrollableFocus<T extends HTMLElement>(forwardRef?: Ref<T>) {
  const [node, setNode] = useState<T | null>(null);
  const [scrollable, setScrollable] = useState(false);
  const ref = useCallback((element: T | null) => {
    setNode(element);
    if (typeof forwardRef === 'function') {
      const cleanup = forwardRef(element);
      if (typeof cleanup === 'function') return () => {setNode(null); cleanup();};
    } else if (forwardRef) {
      forwardRef.current = element;
    }
  }, [forwardRef]);

  useLayoutEffect(() => {
    if (!node) return;
    let frame = 0;
    let active = true;
    const measure = () => {
      frame = 0;
      if (!active) return;
      const style = getComputedStyle(node);
      const vertical = /^(auto|scroll)$/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1;
      const horizontal = /^(auto|scroll)$/.test(style.overflowX) && node.scrollWidth > node.clientWidth + 1;
      setScrollable(vertical || horizontal);
    };
    const schedule = () => {
      if (active && !frame) frame = requestAnimationFrame(measure);
    };
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    const observeContent = () => {
      resize?.disconnect();
      resize?.observe(node);
      for (const child of node.children) resize?.observe(child);
      schedule();
    };
    const mutation = new MutationObserver(observeContent);
    mutation.observe(node, {subtree: true, childList: true, characterData: true,
      attributes: true, attributeFilter: ['class', 'style', 'hidden']});
    observeContent();
    window.addEventListener('resize', schedule);
    document.fonts?.addEventListener('loadingdone', schedule);
    return () => {
      active = false;
      cancelAnimationFrame(frame);
      resize?.disconnect();
      mutation.disconnect();
      window.removeEventListener('resize', schedule);
      document.fonts?.removeEventListener('loadingdone', schedule);
    };
  }, [node]);

  return {ref, tabIndex: scrollable ? 0 : -1};
}
